'use client';

import { useState } from 'react';

/**
 * LazyMapEmbed - click-to-load facade for a Google Maps iframe
 * =============================================================
 *
 * WHAT: Renders a lightweight placeholder card. The real Google Maps
 * <iframe> is only mounted after the visitor clicks "Load map".
 *
 * WHY: A Google Maps embed sets ~35 third-party cookies and pulls in
 * several hundred KB of script the moment it appears in the DOM, even
 * with loading="lazy". Lighthouse penalises this under Best Practices
 * ("Uses third-party cookies") and it hurts LCP/TBT. By deferring the
 * iframe until a deliberate user action we:
 *   - ship zero third-party cookies on page load
 *   - keep the map available for anyone who actually wants it
 *   - avoid needing a cookie-consent banner just for the map
 *
 * HOW: `useState(false)` tracks whether the user opted in. Before that we
 * render a <button> styled as a map card; after that we render the iframe.
 * Because this is a client component the swap happens without a reload.
 */

interface LazyMapEmbedProps {
  /** Full Google Maps embed URL (the `src` you would put on the iframe). */
  src: string;
  /** Accessible title for the iframe once loaded. */
  title: string;
  /** Optional heading shown on the placeholder card. */
  label?: string;
}

export default function LazyMapEmbed({ src, title, label = 'Interactive map' }: LazyMapEmbedProps) {
  const [loaded, setLoaded] = useState(false);

  if (loaded) {
    return (
      <iframe
        src={src}
        title={title}
        referrerPolicy="no-referrer-when-downgrade"
        className="absolute inset-0 w-full h-full border-0"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setLoaded(true)}
      aria-label={`Load ${title}`}
      className="group absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-4 bg-[radial-gradient(ellipse_at_center,_rgba(251,191,36,0.12),_transparent_60%)] cursor-pointer"
    >
      {/* Decorative grid to hint at a map without loading one */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[size:48px_48px]"
      />

      <span
        aria-hidden="true"
        className="relative flex items-center justify-center w-16 h-16 rounded-full bg-amber-400 text-slate-900 text-3xl shadow-lg shadow-amber-400/30 transition-transform group-hover:scale-110"
      >
        📍
      </span>

      <span className="relative text-white text-lg font-semibold">{label}</span>

      <span className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 text-white text-sm font-medium border border-white/20 transition-colors group-hover:bg-amber-400 group-hover:text-slate-900 group-hover:border-amber-400">
        Load map
        <span aria-hidden="true">→</span>
      </span>

      <span className="relative text-white/50 text-xs">
        Loads Google Maps. Google may set cookies.
      </span>
    </button>
  );
}
